// ============================================================
// src/hooks/useDealPipeline.ts
// Refonte complète (voir topo de session) : ce hook lisait/écrivait
// auparavant sur un schéma snake_case parallèle et déconnecté
// (deals.current_stage avec les anciens noms d'étapes, signature NDA
// directe sur dealDocuments sans jamais toucher signedNDAs/
// dealParticipants). Il pointe maintenant sur dealSubmissions, les 11
// vraies étapes du pipeline, et redirige la signature NDA vers le
// chemin canonique (/tools/nda-generator) plutôt que de la dupliquer.
// L'interface exposée (noms de fonctions, forme des types Deal/
// DealDocument en snake_case) est conservée pour ne pas casser les
// composants consommateurs (DealPipelineView, KanbanBoard,
// DealStatisticsCharts, DealDocumentsTable, DealsPipeline.tsx,
// DealFlow.tsx) — seul le contenu réel change.
// ============================================================

import { useState, useCallback } from 'react';
import {
  collection, doc, addDoc, getDoc, getDocs, updateDoc,
  query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

// ── Les 11 vraies étapes du pipeline (alignées sur create-deal.dto.ts /
// DealDetail.tsx). Remplace l'ancienne énumération obsolète. ──────────
export type DealStage =
  | 'submission' | 'pre_evaluation' | 'cim_creation' | 'payment_pending'
  | 'published' | 'lender_submission' | 'nda_signed' | 'buyer_contact'
  | 'first_meeting' | 'loi_signed' | 'closing';

export type DocumentType = 'nda' | 'loi' | 'cim' | 'contract' | 'offer' | 'other';
export type DocumentStatus = 'draft' | 'pending_signature' | 'partially_signed' | 'signed' | 'expired' | 'rejected';

export interface Deal {
  id: string; business_name: string; business_description?: string;
  asking_price?: number; currency: string; seller_id: string;
  broker_id?: string; current_stage: DealStage;
  stage_history: StageHistoryEntry[]; is_confidential: boolean;
  reference_number?: string; created_at: string; updated_at: string;
}

export interface StageHistoryEntry {
  stage: DealStage; timestamp: string; triggered_by?: string; notes?: string;
}

export interface DealDocument {
  id: string; deal_id: string; document_type: DocumentType;
  status: DocumentStatus; document_url?: string; pdf_url?: string;
  required_signers: string[]; signatures: SignatureEntry[];
  buyer_id?: string; seller_id: string;
  document_data?: Record<string, unknown>; document_reference?: string;
  created_at: string; updated_at: string; signed_at?: string; expires_at?: string;
}

export interface SignatureEntry {
  user_id: string; signed_at: string; signature_data: string;
  role: 'buyer' | 'seller' | 'broker';
}

// ── PIPELINE_STAGES : labels + config, alignés sur STAGE_LABELS de
// DealDetail.tsx pour rester cohérents partout dans l'app. ────────────
export const PIPELINE_STAGES: { stage: DealStage; label: string; requiresDocument?: DocumentType }[] = [
  { stage: 'submission', label: 'Submission' },
  { stage: 'pre_evaluation', label: 'Pre-Evaluation' },
  { stage: 'cim_creation', label: 'CIM Creation' },
  { stage: 'payment_pending', label: 'Payment Pending' },
  { stage: 'published', label: 'Published' },
  { stage: 'lender_submission', label: 'Lender Submission' },
  { stage: 'nda_signed', label: 'NDA Signed', requiresDocument: 'nda' },
  { stage: 'buyer_contact', label: 'Buyer Contact' },
  { stage: 'first_meeting', label: 'First Meeting' },
  { stage: 'loi_signed', label: 'LOI Signed', requiresDocument: 'loi' },
  { stage: 'closing', label: 'Closing' },
];

// ── Helpers de conversion : Firestore (dealSubmissions, camelCase,
// rawFormData imbriqué) → forme Deal attendue par les composants. ─────

function toIsoString(value: any): string {
  if (!value) return new Date().toISOString();
  if (typeof value === 'string') return value;
  if (value?.toDate) return value.toDate().toISOString();
  return new Date().toISOString();
}

function mapDealSubmissionToDeal(id: string, d: any): Deal {
  const raw = d.rawFormData || {};
  const general = raw.general || {};
  const financials = raw.financials || {};

  return {
    id,
    business_name: general.legalName || d.legalName || d.businessName || 'Untitled Deal',
    business_description: general.description || d.description || '',
    asking_price: financials.askingPrice ?? d.askingPrice ?? 0,
    currency: financials.currency || 'USD',
    seller_id: d.submittedBy || d.sellerId || '',
    broker_id: d.brokerId || undefined,
    current_stage: (d.currentStage || d.current_stage || d.stage || 'submission') as DealStage,
    stage_history: (d.stageHistory || d.stage_history || []) as StageHistoryEntry[],
    is_confidential: d.isConfidential ?? true,
    reference_number: d.referenceNumber || undefined,
    created_at: toIsoString(d.createdAt || d.submittedAt),
    updated_at: toIsoString(d.updatedAt || d.createdAt || d.submittedAt),
  };
}

function mapDealDocumentSnap(snap: any): DealDocument {
  const d = snap.data();
  return {
    id: snap.id,
    deal_id: d.dealId || d.deal_id || '',
    document_type: (d.documentType || d.document_type || 'other') as DocumentType,
    status: (d.status || 'draft') as DocumentStatus,
    document_url: d.documentUrl || d.document_url,
    pdf_url: d.pdfUrl || d.pdf_url,
    required_signers: (d.requiredSigners || d.required_signers || []).map((s: any) =>
      typeof s === 'string' ? s : s.userId || s.user_id
    ),
    signatures: (d.signatures || []).map((s: any) => ({
      user_id: s.userId || s.user_id,
      signed_at: toIsoString(s.signedAt || s.signed_at),
      signature_data: s.signatureData || s.signature_data || '',
      role: s.role,
    })),
    buyer_id: d.buyerId || d.buyer_id,
    seller_id: d.sellerId || d.seller_id || '',
    document_data: d.documentData || d.document_data,
    document_reference: d.documentReference || d.document_reference,
    created_at: toIsoString(d.createdAt || d.created_at),
    updated_at: toIsoString(d.updatedAt || d.updated_at),
    signed_at: d.signedAt ? toIsoString(d.signedAt) : (d.signed_at || undefined),
    expires_at: d.expiresAt ? toIsoString(d.expiresAt) : (d.expires_at || undefined),
  };
}

export function useDealPipeline() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const getStageIndex = (stage: DealStage) => PIPELINE_STAGES.findIndex(s => s.stage === stage);
  const canProgressTo = (current: DealStage, target: DealStage) =>
    getStageIndex(target) === getStageIndex(current) + 1;

  // ── Lire un deal ─────────────────────────────────────────────
  const fetchDeal = useCallback(async (dealId: string): Promise<Deal | null> => {
    try {
      const snap = await getDoc(doc(db, 'dealSubmissions', dealId));
      if (!snap.exists()) return null;
      return mapDealSubmissionToDeal(snap.id, snap.data());
    } catch (err) { console.error('Error fetching deal:', err); return null; }
  }, []);

  // ── Lire tous les deals ───────────────────────────────────────
  const fetchDeals = useCallback(async (): Promise<Deal[]> => {
    try {
      const snap = await getDocs(
        query(collection(db, 'dealSubmissions'), orderBy('createdAt', 'desc'))
      );
      return snap.docs.map(d => mapDealSubmissionToDeal(d.id, d.data()));
    } catch (err) { console.error('Error fetching deals:', err); return []; }
  }, []);

  // ── Créer un deal ─────────────────────────────────────────────
  // NOTE : la vraie création de deal passe par le formulaire de
  // soumission à 11 étapes (submitDeal(), avec paiement de pré-évaluation
  // requis). Ce raccourci de création manuelle est conservé pour l'usage
  // staff/admin (bouton "Nouveau Dossier" dans DealsPipeline.tsx / 
  // DealFlow.tsx) mais crée un deal directement en submission, sans
  // paiement — à utiliser uniquement pour des dossiers hors-formulaire.
  const createDeal = useCallback(async (dealData: Partial<Deal>): Promise<Deal | null> => {
    if (!user) return null;
    setLoading(true);
    try {
      const now = new Date().toISOString();
      const ref = await addDoc(collection(db, 'dealSubmissions'), {
        legalName: dealData.business_name || 'Untitled Deal',
        description: dealData.business_description || '',
        askingPrice: dealData.asking_price || 0,
        rawFormData: {
          general: { legalName: dealData.business_name || 'Untitled Deal', description: dealData.business_description || '' },
          financials: { askingPrice: dealData.asking_price || 0, currency: dealData.currency || 'USD' },
        },
        submittedBy: user.id,
        brokerId: dealData.broker_id || null,
        currentStage: 'submission',
        stageHistory: [{ stage: 'submission', timestamp: now, triggered_by: user.id }],
        isConfidential: dealData.is_confidential ?? true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast({ title: 'Success', description: 'Deal created successfully' });
      return await fetchDeal(ref.id);
    } catch (err) {
      console.error('Error creating deal:', err);
      toast({ title: 'Error', description: 'Failed to create deal', variant: 'destructive' });
      return null;
    } finally { setLoading(false); }
  }, [user, toast, fetchDeal]);

  // ── Progresser un deal (staff/admin) ────────────────────────────
  // Écrit sur currentStage (le vrai champ), avec historique. Pour un
  // contrôle complet avec prérequis validés côté serveur, préférer
  // l'API NestJS (deals.controller.ts, PATCH /deals/:id/stage).
  const progressDeal = useCallback(async (
    dealId: string, targetStage: DealStage, notes?: string
  ): Promise<boolean> => {
    if (!user) return false;
    setLoading(true);
    try {
      const deal = await fetchDeal(dealId);
      if (!deal) throw new Error('Deal not found');

      const stageConfig = PIPELINE_STAGES.find(s => s.stage === targetStage);
      const newHistory = [...deal.stage_history, { stage: targetStage, timestamp: new Date().toISOString(), triggered_by: user.id, notes }];

      await updateDoc(doc(db, 'dealSubmissions', dealId), {
        currentStage: targetStage, stageHistory: newHistory, updatedAt: serverTimestamp(),
      });

      toast({ title: 'Success', description: `Deal progressed to ${stageConfig?.label}` });
      return true;
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to progress deal', variant: 'destructive' });
      return false;
    } finally { setLoading(false); }
  }, [user, toast, fetchDeal]);

  // ── Vérifier document signé ───────────────────────────────────
  const checkDocumentReady = useCallback(async (dealId: string, docType: DocumentType): Promise<boolean> => {
    if (docType === 'nda') {
      // Source de vérité canonique du point 1 : signedNDAs, pas
      // dealDocuments. Vérifie tous les signataires connus pour ce deal
      // serait idéal, mais faute d'un index dédié on vérifie l'existence
      // d'au moins une signature acheteur pour ce deal.
      try {
        const snap = await getDocs(query(
          collection(db, 'signedNDAs'),
          where('dealId', '==', dealId),
          where('recipientType', '==', 'buyer'),
        ));
        return !snap.empty;
      } catch { return false; }
    }
    try {
      const snap = await getDocs(query(
        collection(db, 'dealDocuments'),
        where('dealId', '==', dealId),
        where('documentType', '==', docType),
        where('status', '==', 'signed')
      ));
      return !snap.empty;
    } catch { return false; }
  }, []);

  // ── Créer un document (LOI, contrat, etc. — pas NDA) ────────────
  const createDocument = useCallback(async (
    dealId: string, docType: DocumentType,
    documentData: Record<string, unknown>, buyerId?: string,
  ): Promise<DealDocument | null> => {
    if (!user) return null;
    if (docType === 'nda') {
      toast({
        title: 'Use the NDA generator',
        description: 'NDAs are created via the NDA tool, not this pipeline.',
        variant: 'destructive',
      });
      return null;
    }
    setLoading(true);
    try {
      const requiredSigners = buyerId ? [user.id, buyerId] : [user.id];
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

      const ref = await addDoc(collection(db, 'dealDocuments'), {
        dealId, documentType: docType,
        status: 'pending_signature', sellerId: user.id,
        buyerId: buyerId || null, requiredSigners,
        signatures: [], documentData,
        expiresAt: expiresAt.toISOString(),
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });

      toast({ title: 'Success', description: `${docType.toUpperCase()} document created` });
      const snap = await getDoc(doc(db, 'dealDocuments', ref.id));
      return mapDealDocumentSnap(snap);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to create document', variant: 'destructive' });
      return null;
    } finally { setLoading(false); }
  }, [user, toast]);

  // ── Signer un document ────────────────────────────────────────
  // NDA : bloqué ici volontairement. Le flux canonique (point 1 de la
  // stabilisation) passe par /tools/nda-generator + la Cloud Function
  // saveSignedNDA, qui met à jour signedNDAs ET dealParticipants (accès
  // Deal Room). Signer un NDA directement ici contournerait ce chaînon
  // et laisserait l'acheteur avec un NDA "signé" mais sans accès réel.
  const signDocument = useCallback(async (
    documentId: string, signatureData: string,
    role: 'buyer' | 'seller' | 'broker',
  ): Promise<boolean> => {
    if (!user) return false;
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'dealDocuments', documentId));
      if (!snap.exists()) throw new Error('Document not found');
      const d = snap.data();

      if ((d.documentType || d.document_type) === 'nda') {
        toast({
          title: 'Use the NDA generator',
          description: 'Please sign this NDA via the NDA tool to ensure Deal Room access is granted correctly.',
          variant: 'destructive',
        });
        return false;
      }

      const currentSignatures = (d.signatures || []) as any[];
      const requiredSigners = (d.requiredSigners || d.required_signers || []) as any[];
      const requiredIds = requiredSigners.map((s: any) => typeof s === 'string' ? s : s.userId || s.user_id);

      if (currentSignatures.some((s: any) => (s.userId || s.user_id) === user.id))
        throw new Error('You have already signed this document');

      const newSignature = {
        userId: user.id, signedAt: new Date().toISOString(), signatureData, role,
      };
      const newSignatures = [...currentSignatures, newSignature];
      const allSigned = requiredIds.every((id: string) => newSignatures.some((s: any) => s.userId === id));

      await updateDoc(doc(db, 'dealDocuments', documentId), {
        signatures: newSignatures,
        status: allSigned ? 'signed' : 'partially_signed',
        ...(allSigned ? { signedAt: new Date().toISOString() } : {}),
        updatedAt: serverTimestamp(),
      });

      toast({ title: 'Success', description: allSigned ? 'Document fully signed!' : 'Signature added, awaiting other parties' });
      return true;
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to sign document', variant: 'destructive' });
      return false;
    } finally { setLoading(false); }
  }, [user, toast]);

  // ── Lire documents d'un deal ──────────────────────────────────
  const fetchDealDocuments = useCallback(async (dealId: string): Promise<DealDocument[]> => {
    try {
      const snap = await getDocs(query(
        collection(db, 'dealDocuments'),
        where('dealId', '==', dealId),
        orderBy('createdAt', 'desc')
      ));
      return snap.docs.map(mapDealDocumentSnap);
    } catch { return []; }
  }, []);

  // ── Déplacer un deal (Kanban) ─────────────────────────────────
  const moveDealToStage = useCallback(async (
    dealId: string, targetStage: DealStage, notes?: string
  ): Promise<boolean> => {
    if (!user) return false;
    setLoading(true);
    try {
      const deal = await fetchDeal(dealId);
      if (!deal) throw new Error('Deal not found');
      if (deal.current_stage === targetStage) return true;

      const stageConfig = PIPELINE_STAGES.find(s => s.stage === targetStage);
      const newHistory = [...deal.stage_history, { stage: targetStage, timestamp: new Date().toISOString(), triggered_by: user.id, notes: notes || 'Moved via Kanban' }];

      await updateDoc(doc(db, 'dealSubmissions', dealId), {
        currentStage: targetStage, stageHistory: newHistory, updatedAt: serverTimestamp(),
      });

      toast({ title: 'Success', description: `Deal moved to ${stageConfig?.label}` });
      return true;
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to move deal', variant: 'destructive' });
      return false;
    } finally { setLoading(false); }
  }, [user, toast, fetchDeal]);

  return {
    loading, fetchDeal, fetchDeals, createDeal, progressDeal, moveDealToStage,
    createDocument, signDocument, fetchDealDocuments, checkDocumentReady,
    getStageIndex, canProgressTo, PIPELINE_STAGES,
  };
}